import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, Loader2, Plus, X, Mic, MicOff } from 'lucide-react';

export default function AddDestinationModal({
  open, onOpenChange,
  searchQuery, setSearchQuery,
  searchResults, searchLoading,
  destName, setDestName,
  selectedResult, setSelectedResult,
  addDestination,
  voiceSupported, isListening, startVoiceInput,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) { setSearchQuery(''); setDestName(''); setSelectedResult(null); }
    }}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg p-0 gap-0 top-[5%] sm:top-[50%] translate-y-0 sm:-translate-y-1/2 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle>Add Destination</DialogTitle>
          <DialogDescription>{selectedResult ? 'Enter a name for this location' : 'Search for a postcode or address'}</DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-4">
          {!selectedResult ? (
            <>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868b]" />
                  <Input type="text" placeholder="Search postcode or address..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-12 pl-10 pr-4 text-base" data-testid="admin-search-input" autoFocus />
                </div>
                {voiceSupported && (
                  <Button type="button" variant={isListening ? "default" : "outline"} size="icon" onClick={() => startVoiceInput((text) => setSearchQuery(text))} className={`h-12 w-12 flex-shrink-0 rounded-xl transition-all ${isListening ? 'bg-[#ff3b30] hover:bg-[#ff3b30] animate-pulse' : ''}`} data-testid="voice-search-btn">
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </Button>
                )}
              </div>
              {isListening && <div className="flex items-center justify-center gap-2 py-2 text-sm text-[#ff3b30]"><div className="w-2 h-2 bg-[#ff3b30] rounded-full animate-pulse" />Recording... tap again to stop</div>}
              {searchLoading && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin" /></div>}
              {searchResults.length > 0 && (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {searchResults.map((result, i) => (
                    <button key={i} onClick={() => setSelectedResult(result)} className="w-full p-3 text-left bg-muted/50 hover:bg-muted border border-border transition-colors" data-testid={`search-result-${i}`}>
                      <div className="font-mono font-bold">{result.postcode || result.display}</div>
                      <div className="text-sm text-muted-foreground truncate">{result.full_address || result.display}</div>
                      {result.distance_km && <div className="text-xs text-primary mt-1">{result.distance_km} km away</div>}
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length > 0 && searchResults.length === 0 && !searchLoading && <p className="text-sm text-muted-foreground text-center py-4">No results found</p>}
            </>
          ) : (
            <>
              <div className="p-3 bg-muted rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono font-bold">{selectedResult.postcode || selectedResult.display}</div>
                    <div className="text-sm text-muted-foreground">{selectedResult.full_address || selectedResult.display}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedResult(null)} className="p-1"><X className="w-4 h-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Name <span className="text-[#ff3b30]">*</span></Label>
                <div className="flex gap-2">
                  <Input type="text" placeholder="Enter name for this stop" value={destName} onChange={(e) => setDestName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && destName.trim()) addDestination(selectedResult); }} className="h-12 text-base flex-1" data-testid="dest-name-input" autoFocus />
                  {voiceSupported && (
                    <Button type="button" variant={isListening ? "default" : "outline"} size="icon" onClick={() => startVoiceInput((text) => setDestName(text))} className={`h-12 w-12 flex-shrink-0 rounded-xl transition-all ${isListening ? 'bg-[#ff3b30] hover:bg-[#ff3b30] animate-pulse' : ''}`} data-testid="voice-name-btn">
                      {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </Button>
                  )}
                </div>
                {isListening && <div className="flex items-center gap-2 mt-2 text-sm text-[#ff3b30]"><div className="w-2 h-2 bg-[#ff3b30] rounded-full animate-pulse" />Recording... tap again to stop</div>}
              </div>
              <Button onClick={() => addDestination(selectedResult)} disabled={!destName.trim()} className="w-full h-12 text-base font-bold"><Plus className="w-5 h-5 mr-2" />Add to Route</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
